// Restaurant tables management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { RestaurantTable } from '@/types/entities'

/**
 * Fetch all tables for a specific restaurant
 * Uses RPC function to bypass RLS for staff access
 */
export function useTables(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.tables.list(restaurantId || ''),
    staleTime: 2 * 60 * 1000, // 2 minutes - tables don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Tables don't need real-time updates
    refetchOnMount: false, // Don't refetch if data exists (useOrders also fetches tables)
    queryFn: async (): Promise<RestaurantTable[]> => {
      if (!restaurantId) {
        return []
      }

      // Use RPC function to bypass RLS (allows staff to view tables)
      const { data, error } = await supabase
        .rpc('get_restaurant_tables', { p_restaurant_id: restaurantId })

      if (error) {
        throw error
      }

      return (data || []).map((t: any): RestaurantTable => {
        const table: RestaurantTable = {
          id: t.id,
          tableNumber: t.table_number ?? t.tableNumber,
          seats: t.seats,
          zoneId: t.zone_id ?? null,
          status: t.status || 'available',
          createdAt: t.created_at ?? t.createdAt ?? '',
          updatedAt: t.updated_at ?? t.updatedAt ?? '',
        }
        if (t.zone_name_ar || t.zone_name_fr) {
          table.zone = {
            id: t.zone_id,
            restaurantId: '',
            nameAr: t.zone_name_ar || '',
            nameFr: t.zone_name_fr || '',
            isActive: true,
            sortOrder: 0,
            createdAt: '',
            updatedAt: '',
          }
        }
        return table
      })
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch single table by ID
 * Uses RPC function to bypass RLS for staff access
 */
export function useTable(tableId: string) {
  return useQuery({
    queryKey: queryKeys.tables.detail(tableId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<RestaurantTable> => {
      // Use RPC function to bypass RLS (allows staff to view table)
      const { data, error } = await supabase
        .rpc('get_table_by_id', { p_table_id: tableId })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) {
        throw new Error('Table not found')
      }

      const t = data[0] as any
      if (!t) {
        throw new Error('Table not found')
      }
      const table: RestaurantTable = {
        id: t.id,
        tableNumber: t.table_number ?? t.tableNumber,
        seats: t.seats,
        zoneId: t.zone_id ?? null,
        status: t.status || 'available',
        createdAt: t.created_at ?? t.createdAt ?? '',
        updatedAt: t.updated_at ?? t.updatedAt ?? '',
      }
      if (t.zone_name_ar || t.zone_name_fr) {
        table.zone = {
          id: t.zone_id,
          restaurantId: '',
          nameAr: t.zone_name_ar || '',
          nameFr: t.zone_name_fr || '',
          isActive: true,
          sortOrder: 0,
          createdAt: '',
          updatedAt: '',
        }
      }
      return table 
    },
    enabled: !!tableId,
  })
}

/**
 * Create new table mutation
 */
export function useCreateTable(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      tableNumber: number
      seats: number
      zoneId?: string | null
    }) => {
      const { data, error } = await supabase
        .from('tables')
        .insert({
          restaurant_id: restaurantId,
          table_number: request.tableNumber,
          seats: request.seats,
          zone_id: request.zoneId || null,
        })
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.list(restaurantId) })
    },
  })
}

/**
 * Create multiple tables mutation
 */
export function useCreateMultipleTables(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      startNumber: number
      endNumber: number
      seats: number
      zoneId?: string | null
    }) => {
      const tables = []
      for (let i = request.startNumber; i <= request.endNumber; i++) {
        tables.push({
          restaurant_id: restaurantId,
          table_number: i,
          seats: request.seats,
          zone_id: request.zoneId || null,
        })
      }

      const { data, error } = await supabase
        .from('tables')
        .insert(tables)
        .select()

      if (error) {
        // Check for duplicate table number error
        if (error.code === '23505' && error.message.includes('tables_number_restaurant_unique')) {
          throw new Error('DUPLICATE_TABLE_NUMBER')
        }
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.list(restaurantId) })
    },
  })
}

/**
 * Update table mutation
 */
export function useUpdateTable(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      id: string
      tableNumber?: number
      seats?: number
      zoneId?: string | null
    }) => {
      const updateData: {
        table_number?: number
        seats?: number
        zone_id?: string | null
      } = {}

      if (request.tableNumber !== undefined) updateData.table_number = request.tableNumber
      if (request.seats !== undefined) updateData.seats = request.seats
      if (request.zoneId !== undefined) updateData.zone_id = request.zoneId || null

      const { data, error } = await supabase
        .from('tables')
        .update(updateData)
        .eq('id', request.id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.list(restaurantId) })
    },
  })
}

/**
 * Delete table mutation
 */
export function useDeleteTable(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tableId: string) => {
      const { error } = await supabase
        .from('tables')
        .delete()
        .eq('id', tableId)

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          throw new Error('TABLE_IN_USE')
        }
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.list(restaurantId) })
    },
  })
}

/**
 * Update table status mutation
 */
export function useUpdateTableStatus(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      id: string
      status: 'available' | 'occupied'
    }) => {
      const { data, error } = await supabase
        .from('tables')
        .update({ status: request.status })
        .eq('id', request.id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.list(restaurantId) })
    },
  })
}
